import {
  CountryDto,
  CountryCostBlock,
  CountryFaqItem,
  CountryPageStat,
  CountryScholarship,
  CountryTimelineStep,
  CountryWhyCard,
  CreateCountryPayload,
} from '../../../shared/services/countries.service';

type LooseRecord = Record<string, unknown>;

function asString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();
}

function normalizeWhyCards(raw: unknown): CountryWhyCard[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      const row = item as LooseRecord;
      const title = asString(row['title']);
      const description = asString(row['description']);
      if (!title || !description) return null;
      const imageMediaAssetId = asString(row['imageMediaAssetId']);
      const imagePath = asString(row['imagePath']);
      return {
        title,
        description,
        ...(imageMediaAssetId ? { imageMediaAssetId } : {}),
        ...(imagePath ? { imagePath } : {}),
      };
    })
    .filter((x): x is CountryWhyCard => x !== null);
}

function normalizeCostBlocks(raw: unknown): CountryCostBlock[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      const row = item as LooseRecord;
      const title = asString(row['title']);
      const body = asString(row['body'] ?? row['description']);
      if (!title || !body) return null;
      const icon = asString(row['icon']);
      return { title, body, ...(icon ? { icon } : {}) };
    })
    .filter((x): x is CountryCostBlock => x !== null);
}

function normalizeScholarships(raw: unknown): CountryScholarship[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      const row = item as LooseRecord;
      const name = asString(row['name'] ?? row['title']);
      if (!name) return null;
      const provider = asString(row['provider']);
      const summary = asString(row['summary'] ?? row['description']);
      const ctaUrl = asString(row['ctaUrl']);
      return {
        name,
        ...(provider ? { provider } : {}),
        ...(summary ? { summary } : {}),
        ...(ctaUrl ? { ctaUrl } : {}),
      };
    })
    .filter((x): x is CountryScholarship => x !== null);
}

function normalizeVisaSteps(raw: unknown): CountryTimelineStep[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      const row = item as LooseRecord;
      const title = asString(row['title']);
      const description = asString(row['description']);
      if (!title || !description) return null;
      return { title, description };
    })
    .filter((x): x is CountryTimelineStep => x !== null);
}

function normalizeFaqItems(raw: unknown): CountryFaqItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      const row = item as LooseRecord;
      const question = asString(row['question']);
      const answer = asString(row['answer']);
      if (!question || !answer) return null;
      return { question, answer };
    })
    .filter((x): x is CountryFaqItem => x !== null);
}

export type CountryWhyCardFormItem = {
  key: string;
  title: string;
  description: string;
  imageMediaAssetId: string;
};

export const MAX_WHY_CARDS = 3;

export function newWhyCardKey(): string {
  return `why-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function whyCardsFromDto(
  cards: CountryWhyCard[] | undefined,
  previous: CountryWhyCardFormItem[] = [],
): CountryWhyCardFormItem[] {
  return (cards ?? []).slice(0, MAX_WHY_CARDS).map((card, index) => ({
    key: previous[index]?.key ?? newWhyCardKey(),
    title: card.title ?? '',
    description: card.description ?? '',
    imageMediaAssetId:
      card.imageMediaAssetId != null ? String(card.imageMediaAssetId).trim() : '',
  }));
}

export function whyCardsToPayload(cards: CountryWhyCardFormItem[]): CountryWhyCard[] {
  return normalizeWhyCards(
    cards.slice(0, MAX_WHY_CARDS).map((card) => ({
      title: card.title,
      description: card.description,
      imageMediaAssetId: card.imageMediaAssetId,
    })),
  );
}

export type CountryFormModel = Omit<CreateCountryPayload, 'whyCards'> & {
  highlightsCsv: string;
  popularCoursesCsv: string;
  keywordsCsv: string;
  heroEyebrow: string;
  heroTitle: string;
  heroLede: string;
  heroPrimaryCtaText: string;
  heroPrimaryCtaAnchor: string;
  heroMediaAssetId: string;
  pageStatsText: string;
  whyEyebrow: string;
  whyTitle: string;
  whySubtitle: string;
  whyCards: CountryWhyCardFormItem[];
  costsTitle: string;
  costBlocksJson: string;
  scholarshipsJson: string;
  visaTitle: string;
  visaSubtitle: string;
  visaStepsJson: string;
  faqItemsJson: string;
};

/** Full costs section for admin JSON textarea (intro + line items + blocked account card). */
export const DEFAULT_COSTS_SECTION_JSON = JSON.stringify(
  {
    intro:
      'Studying in Germany is remarkably affordable compared to the US or UK. Here is a breakdown of what you can expect.',
    items: [
      { title: 'Tuition Fees', body: '€0 - €1,500/year at Public Universities', icon: 'wallet' },
      { title: 'Living Expenses', body: '€850 - €1,200/month (Rent, Food, Social)', icon: 'home' },
      {
        title: 'Health Insurance',
        body: 'Approx. €110/month (Statutory Insurance)',
        icon: 'shield',
      },
    ],
    blockedAccount: {
      badge: 'Mandatory',
      title: 'Blocked Account',
      amount: '€11,208',
      amountSuffix: '/ year',
      description:
        'To obtain a student visa, you must provide proof of financial resources by depositing funds into a blocked account.',
      bullets: ['Monthly payout: €934', 'Refundable if visa is rejected'],
    },
  },
  null,
  2,
);

type ParsedBlockedAccount = {
  badge?: string;
  title?: string;
  amount?: string;
  amountSuffix?: string;
  description?: string;
  bullets?: string[];
};

type ParsedCostsSection = {
  intro?: string;
  costBlocks: CountryCostBlock[];
  blocked?: ParsedBlockedAccount;
};

function parseBlockedAccount(raw: unknown): ParsedBlockedAccount | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const row = raw as LooseRecord;
  const bullets = Array.isArray(row['bullets'])
    ? row['bullets'].map((item) => asString(item)).filter(Boolean)
    : [];
  const blocked: ParsedBlockedAccount = {
    badge: asString(row['badge']) || undefined,
    title: asString(row['title']) || undefined,
    amount: asString(row['amount']) || undefined,
    amountSuffix: asString(row['amountSuffix']) || undefined,
    description: asString(row['description'] ?? row['body']) || undefined,
    bullets: bullets.length ? bullets : undefined,
  };
  if (
    !blocked.badge &&
    !blocked.title &&
    !blocked.amount &&
    !blocked.description &&
    !blocked.bullets?.length
  ) {
    return undefined;
  }
  return blocked;
}

function parseCostsSectionJson(text: string, fieldLabel: string): ParsedCostsSection {
  let raw: unknown;
  try {
    raw = JSON.parse(text || '{}');
  } catch {
    throw new Error(fieldLabel);
  }

  if (Array.isArray(raw)) {
    return { costBlocks: normalizeCostBlocks(raw) };
  }

  if (!raw || typeof raw !== 'object') {
    throw new Error(fieldLabel);
  }

  const row = raw as LooseRecord;
  const intro = asString(row['intro']);
  const items = normalizeCostBlocks(row['items'] ?? row['lineItems'] ?? []);
  const blocked = parseBlockedAccount(row['blockedAccount'] ?? row['blocked']);
  return {
    intro: intro || undefined,
    costBlocks: items,
    blocked,
  };
}

export function buildCostsSectionJson(country: CountryDto): string {
  const blocks = country.costBlocks ?? [];
  const items = blocks.filter(
    (b) => !/^overview$/i.test(b.title.trim()) && !/blocked\s*account/i.test(b.title),
  );
  const intro =
    country.costsIntro?.trim() ||
    blocks.find((b) => /^overview$/i.test(b.title.trim()))?.body?.trim() ||
    '';

  const hasBlockedFields =
    !!country.blockedAccountAmount?.trim() ||
    !!country.blockedAccountDescription?.trim() ||
    (country.blockedAccountBullets?.length ?? 0) > 0;

  const blockedAccount = hasBlockedFields
    ? {
        badge: country.blockedAccountBadge ?? 'Mandatory',
        title: country.blockedAccountTitle ?? 'Blocked Account',
        amount: country.blockedAccountAmount ?? '',
        amountSuffix: country.blockedAccountAmountSuffix ?? '/ year',
        description: country.blockedAccountDescription ?? '',
        bullets: country.blockedAccountBullets ?? [],
      }
    : undefined;

  return JSON.stringify({ intro, items, ...(blockedAccount ? { blockedAccount } : {}) }, null, 2);
}

export const emptyCountryForm = (): CountryFormModel => ({
  slug: '',
  name: '',
  shortDescription: '',
  flagIcon: '',
  highlights: [],
  highlightsCsv: '',
  popularCoursesCsv: '',
  keywordsCsv: '',
  stats: { avgFees: '', visaSuccessRate: '', popularCourses: [] },
  seo: { title: '', description: '', keywords: [] },
  status: 'DRAFT',
  heroEyebrow: '',
  heroTitle: '',
  heroLede: '',
  heroPrimaryCtaText: '',
  heroPrimaryCtaAnchor: '',
  heroMediaAssetId: '',
  pageStatsText: defaultPageStatsText(),
  whyEyebrow: '',
  whyTitle: '',
  whySubtitle: '',
  whyCards: [],
  costsTitle: 'Understanding the Costs',
  costBlocksJson: DEFAULT_COSTS_SECTION_JSON,
  scholarshipsJson: '[]',
  visaTitle: '',
  visaSubtitle: '',
  visaStepsJson: '[]',
  faqItemsJson: '[]',
});

/** One-line template for the stats strip textarea (value|label|icon). */
export const DEFAULT_PAGE_STATS_TEXT = [
  '€0|Tuition Fees|wallet',
  '18M|Post-Study Work|briefcase',
  '€45k+|Avg. Starting Salary|trending',
  'STEM|Top Ranking|award',
].join('\n');

export function defaultPageStatsText(): string {
  return DEFAULT_PAGE_STATS_TEXT;
}

function ensurePageStatsText(text: string): string {
  return text.trim() ? text : defaultPageStatsText();
}

export function parsePageStats(text: string): CountryPageStat[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split('|').map((s) => s.trim());
      const value = parts[0] ?? '';
      const label = parts[1] ?? '';
      const icon = parts[2];
      if (!value || !label) return null;
      return { value, label, ...(icon ? { icon } : {}) };
    })
    .filter((x): x is CountryPageStat => x !== null);
}

export function countryToFormModel(country: CountryDto): CountryFormModel {
  const pageStats = country.pageStats ?? [];
  const pageStatsText = ensurePageStatsText(
    pageStats
      .map((s) => [s.value, s.label, s.icon ?? ''].filter((x) => x !== '').join('|'))
      .join('\n'),
  );
  return {
    ...emptyCountryForm(),
    slug: country.slug,
    name: country.name,
    shortDescription: country.shortDescription ?? '',
    flagIcon: country.flagIcon ?? '',
    heroImageId: country.heroImageId,
    stats: {
      avgFees: country.stats?.avgFees ?? '',
      visaSuccessRate: country.stats?.visaSuccessRate ?? '',
      popularCourses: country.stats?.popularCourses ?? [],
    },
    seo: {
      title: country.seo?.title ?? '',
      description: country.seo?.description ?? '',
      keywords: country.seo?.keywords ?? [],
      ogImageId: country.seo?.ogImageId,
    },
    highlightsCsv: (country.highlights ?? []).join(', '),
    popularCoursesCsv: (country.stats?.popularCourses ?? []).join(', '),
    keywordsCsv: (country.seo?.keywords ?? []).join(', '),
    status: country.status ?? 'DRAFT',
    heroEyebrow: country.heroEyebrow ?? '',
    heroTitle: country.heroTitle ?? '',
    heroLede: country.heroLede ?? '',
    heroPrimaryCtaText: country.heroPrimaryCtaText ?? '',
    heroPrimaryCtaAnchor: country.heroPrimaryCtaAnchor ?? '',
    heroMediaAssetId:
      country.heroMediaAssetId != null ? String(country.heroMediaAssetId).trim() : '',
    pageStatsText,
    whyEyebrow: country.whyEyebrow ?? '',
    whyTitle: country.whyTitle ?? '',
    whySubtitle: country.whySubtitle ?? '',
    whyCards: whyCardsFromDto(country.whyCards),
    costsTitle: country.costsTitle ?? 'Understanding the Costs',
    costBlocksJson: buildCostsSectionJson(country),
    scholarshipsJson: JSON.stringify(country.scholarships ?? [], null, 2),
    visaTitle: country.visaTitle ?? '',
    visaSubtitle: country.visaSubtitle ?? '',
    visaStepsJson: JSON.stringify(country.visaSteps ?? [], null, 2),
    faqItemsJson: JSON.stringify(country.faqItems ?? [], null, 2),
  };
}

function parseJsonArray(text: string, fieldLabel: string): unknown {
  try {
    return JSON.parse(text || '[]');
  } catch {
    throw new Error(fieldLabel);
  }
}

/** URL slug from display name (e.g. "Study in France" → "study-in-france"). */
export function slugFromName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Clone all marketing fields from a country; caller supplies new name and slug. */
export function countryToDuplicatePayload(
  source: CountryDto,
  name: string,
  slug: string,
): CreateCountryPayload {
  const form = countryToFormModel(source);
  form.name = name.trim();
  form.slug = slug;
  form.status = 'DRAFT';
  return formModelToPayload(form);
}

export function formModelToPayload(form: CountryFormModel): CreateCountryPayload {
  const whyCards = whyCardsToPayload(form.whyCards);
  const costsSection = parseCostsSectionJson(form.costBlocksJson, 'Costs');
  const costBlocks = costsSection.costBlocks;
  const blocked = costsSection.blocked;
  const scholarships = normalizeScholarships(parseJsonArray(form.scholarshipsJson, 'Scholarships'));
  const visaSteps = normalizeVisaSteps(parseJsonArray(form.visaStepsJson, 'Visa journey'));
  const faqItems = normalizeFaqItems(parseJsonArray(form.faqItemsJson, 'FAQ'));

  return {
    slug: form.slug,
    name: form.name,
    shortDescription: form.shortDescription || undefined,
    flagIcon: form.flagIcon || undefined,
    highlights: form.highlightsCsv.split(',').map((s) => s.trim()).filter(Boolean),
    stats: {
      avgFees: form.stats?.avgFees || undefined,
      visaSuccessRate: form.stats?.visaSuccessRate || undefined,
      popularCourses: form.popularCoursesCsv.split(',').map((s) => s.trim()).filter(Boolean),
    },
    seo: {
      title: form.seo.title || form.name,
      description: form.seo.description || form.shortDescription || form.name,
      keywords: form.keywordsCsv.split(',').map((s) => s.trim()).filter(Boolean),
      ogImageId: form.seo.ogImageId,
    },
    status: form.status,
    heroEyebrow: form.heroEyebrow || undefined,
    heroTitle: form.heroTitle || undefined,
    heroLede: form.heroLede || undefined,
    heroPrimaryCtaText: form.heroPrimaryCtaText || undefined,
    heroPrimaryCtaAnchor: form.heroPrimaryCtaAnchor || undefined,
    heroMediaAssetId: form.heroMediaAssetId.trim() || undefined,
    pageStats: parsePageStats(form.pageStatsText),
    whyEyebrow: form.whyEyebrow || undefined,
    whyTitle: form.whyTitle || undefined,
    whySubtitle: form.whySubtitle || undefined,
    whyCards,
    costsTitle: form.costsTitle || undefined,
    costsIntro: costsSection.intro,
    costBlocks,
    blockedAccountBadge: blocked?.badge,
    blockedAccountTitle: blocked?.title,
    blockedAccountAmount: blocked?.amount,
    blockedAccountAmountSuffix: blocked?.amountSuffix,
    blockedAccountDescription: blocked?.description,
    blockedAccountBullets: blocked?.bullets?.length ? blocked.bullets : undefined,
    scholarships,
    visaTitle: form.visaTitle || undefined,
    visaSubtitle: form.visaSubtitle || undefined,
    visaSteps,
    faqItems,
  };
}
